import HtmlUtils from "html-utils";
import _ from "underscore";
import s from "underscore.string";

function textAreaTag(name, value, html_options) {
  html_options = html_options || {};
  html_options.id = html_options.id || name;
  html_options.name = html_options.name || name;
  value = value || "";
  if (html_options.size) {
    html_options.cols = html_options.size.split("x")[0];
    html_options.rows = html_options.size.split("x")[1];
    delete html_options.size;
  }

  html_options.cols = html_options.cols || 50;
  html_options.rows = html_options.rows || 4;

  return (
    this.start_tag_for("textarea", html_options) +
    value +
    this.tag_end("textarea")
  );
}

/**
 * Class which supplies functions to the view rendering engine.
 * @constructor
 * @module app/ejs
 *
 * @param {Object} data The data passed to the view.  Helpers have access to it through this._data
 * @param {Object} extras Any additional attributes to place on the instance created.
 */
var Helper = function (data, extras) {
  this._data = data;
  this._extras = extras;
  _.extend(this, extras);
  _.bindAll.apply(_, [this].concat(_.functions(this)));
};
/* @prototype*/
Helper.prototype = {
  /**
   * For a given value, tries to create a human representation.
   * @param {Object} input the value being converted.
   * @param {Object} null_text what text should be present if input == null or undefined, defaults to ''
   * @return {String}
   */
  to_text: function (input, null_text) {
    if (input === null || input === undefined) {
      return null_text || "";
    }
    if (input instanceof Date) {
      return input.toDateString();
    }
    if (input.toString) {
      return input.toString().replace(/\n/g, "<br />").replace(/''/g, "'");
    }
    return "";
  },

  /**
   * Create HTML which displays a date via a set of select elements.
   * @param  {string} name         The date field name.
   * @param  {Date} [value]        The date value.
   * @return {string}              The resulting HTML.
   */
  date_tag: function (name, value, html_options) {
    if (!(value instanceof Date)) {
      value = new Date();
    }

    var month_names = [
      "January",
      "February",
      "March",
      "April",
      "May",
      "June",
      "July",
      "August",
      "September",
      "October",
      "November",
      "December",
    ];
    var years = [],
      months = [],
      days = [];
    var year = value.getFullYear();
    var month = value.getMonth();
    var day = value.getDate();
    for (var y = year - 15; y < year + 15; y++) {
      years.push({ value: y, text: y });
    }
    for (var m = 0; m < 12; m++) {
      months.push({ value: m, text: month_names[m] });
    }
    for (var d = 0; d < 31; d++) {
      days.push({ value: d + 1, text: d + 1 });
    }
    var year_select = this.select_tag(name + "[year]", year, years, {
      id: name + "[year]",
    });
    var month_select = this.select_tag(name + "[month]", month, months, {
      id: name + "[month]",
    });
    var day_select = this.select_tag(name + "[day]", day, days, {
      id: name + "[day]",
    });

    return year_select + month_select + day_select;
  },

  /**
   * Creates HTML to open a form tag
   * @param  {string} action       The form action.
   * @param  {object} html_options Options for the form tag.
   * @return {string}              The resulting HTML.
   */
  form_tag: function (action, html_options) {
    html_options = html_options || {};
    html_options.action = action;
    if (html_options.multipart) {
      html_options.method = "post";
      html_options.enctype = "multipart/form-data";
    }

    return this.start_tag_for("form", html_options);
  },

  /**
   * Generate a closing form tag.
   * @return {string}   The resulting HTML.
   */
  form_tag_end: function () {
    return this.tag_end("form");
  },

  /**
   * Generate a hidden input tag.
   * @param  {string} name         The field name.
   * @param  {string} value        The field value.
   * @param  {object} html_options Options for the tag.
   * @return {string}              The resulting HTML.
   */
  hidden_field_tag: function (name, value, html_options) {
    return this.input_field_tag(name, value, "hidden", html_options);
  },

  /**
   * Generate an input field tag.
   * @param  {string} name         The field name.
   * @param  {string} value        The field value.
   * @param  {string} inputType    The type of input field.
   * @param  {object} html_options Options for the tag.
   * @return {string}              The resulting HTML.
   */
  input_field_tag: function (name, value, inputType, html_options) {
    html_options = html_options || {};
    html_options.id = html_options.id || name;
    html_options.value = value || "";
    html_options.type = inputType || "text";
    html_options.name = name;

    return this.single_tag_for("input", html_options);
  },

  /**
   * Gets a value indicating whether or not the current URL matches the given URL.
   * @param  {string}  url The URL to match.
   * @return {Boolean}     True for a match, otherwise false.
   */
  is_current_page: function (url) {
    return window.location.href == url || window.location.pathname == url
      ? true
      : false;
  },

  /**
   * Generate a link to a URL.
   * @param  {string} name         The link text
   * @param  {string} url          The link URL
   * @param  {object} html_options Options for the tag.
   * @param {object} html_options.confirm Add a confirmation box before navigation.
   * @return {string}              The resulting HTML.
   */
  link_to: function (name, url, html_options) {
    if (!name) {
      name = "null";
    }
    if (!html_options) {
      html_options = {};
    }

    if (html_options.confirm) {
      html_options.onclick =
        ' var ret_confirm = confirm("' +
        html_options.confirm +
        '"); if(!ret_confirm){ return false;} ';
      html_options.confirm = null;
    }
    html_options.href = url;
    return this.start_tag_for("a", html_options) + name + this.tag_end("a");
  },

  /**
   * Generate a submit input field to a URL.
   * @param  {string} name         The link text.
   * @param  {string} url          The link url.
   * @param  {object} html_options Options for the tag.
   * @param {object} html_options.confirm Add a confirmation box before navigation.
   * @return {string}              The resulting HTML.
   */
  submit_link_to: function (name, url, html_options) {
    if (!name) {
      name = "null";
    }
    if (!html_options) {
      html_options = {};
    }
    html_options.onclick = html_options.onclick || "";

    if (html_options.confirm) {
      html_options.onclick =
        ' var ret_confirm = confirm("' +
        html_options.confirm +
        '"); if(!ret_confirm){ return false;} ';
      html_options.confirm = null;
    }

    html_options.value = name;
    html_options.type = "submit";
    html_options.onclick =
      html_options.onclick + (url ? this.url_for(url) : "") + "return false;";
    //html_options.href='#'+(options ? Routes.url_for(options) : '')
    return this.start_tag_for("input", html_options);
  },

  /**
   * Generate a link to the given URL if `condition` is true.
   * @param  {Boolean} condition    The condition to check.
   * @param  {string} name         The link text.
   * @param  {string} url          The link URL.
   * @param  {object} html_options Options for the tag
   * @param  {Function} block        Function to generate source if `conditiion` is false.
   * @return {string}              The resulting HTML.
   */
  link_to_if: function (condition, name, url, html_options, block) {
    return this.link_to_unless(!condition, name, url, html_options, block);
  },

  /**
   * Generate a link to the given URL if `condition` is false.
   * @param  {Boolean} condition    The condition to check.
   * @param  {string} name         The link text.
   * @param  {string} url          The link URL.
   * @param  {object} html_options Options for the tag
   * @param  {Function} block        Function to generate source if `conditiion` is true.
   * @return {string}              The resulting HTML.
   */
  link_to_unless: function (condition, name, url, html_options, block) {
    html_options = html_options || {};
    if (condition) {
      if (block && typeof block == "function") {
        return block(name, url, html_options, block);
      } else {
        return name;
      }
    } else {
      return this.link_to(name, url, html_options);
    }
  },

  /**
   * Generate a link to the given URL if we are not already on that page.
   * @param  {string} name         The link text.
   * @param  {string} url          The link URL.
   * @param  {object} html_options Options for the tag
   * @param  {Function} block        Function to generate source if we are on the page.
   * @return {string}              The resulting HTML.
   */
  link_to_unless_current: function (name, url, html_options, block) {
    html_options = html_options || {};
    return this.link_to_unless(
      this.is_current_page(url),
      name,
      url,
      html_options,
      block
    );
  },

  /**
   * Generate a password tag.
   * @param  {string} name          The field name
   * @param  {string} value         The field value
   * @param  {object} html_options    Options for the tag.
   * @return {string}               The resulting HTML.
   */
  password_field_tag: function (name, value, html_options) {
    return this.input_field_tag(name, value, "password", html_options);
  },

  /**
   * Generate a select tag.
   * @param  {string} name         The field name.
   * @param  {string} value        The field value.
   * @param  {Array} choices      The possible selections.
   * @param {string} choices[].value The selection value.
   * @param {string} choices[].text The selection display text.
   * @param  {object} html_options Options for the tag.
   * @return {string}              The resulting HTML.
   */
  select_tag: function (name, value, choices, html_options) {
    html_options = html_options || {};
    html_options.id = html_options.id || name;
    html_options.value = value;
    html_options.name = name;

    var txt = "";
    txt += this.start_tag_for("select", html_options);

    for (var i = 0; i < choices.length; i++) {
      var choice = choices[i];
      var optionOptions = { value: choice.value };
      if (choice.value == value) {
        optionOptions.selected = "selected";
      }
      txt +=
        this.start_tag_for("option", optionOptions) +
        choice.text +
        this.tag_end("option");
    }
    txt += this.tag_end("select");
    return txt;
  },

  /**
   * Generates a self-closing tag.
   * @param  {string} tag          The tag name (e.g. "div").
   * @param  {object} html_options Options for the tag.
   * @return {string}              The resulting HTML.
   */
  single_tag_for: function (tag, html_options) {
    return this.tag(tag, html_options, "/>");
  },

  /**
   * Generates a start tag.
   * @param  {string} tag          The tag name.
   * @param  {object} html_options Options for the tag.
   * @return {string}              The resulting HTML.
   */
  start_tag_for: function (tag, html_options) {
    return this.tag(tag, html_options);
  },

  /**
   * Generates a submit tag.
   * @param  {string} name         The submit button text.
   * @param  {object} html_options Options for the tag.
   * @return {string}              The resulting HTML.
   */
  submit_tag: function (name, html_options) {
    html_options = html_options || {};
    //html_options.name  = html_options.id  || 'commit';
    html_options.type = html_options.type || "submit";
    html_options.value = name || "Submit";
    return this.single_tag_for("input", html_options);
  },

  /**
   * Generates a tag.
   * @param  {string} tag          The tag name.
   * @param  {object} html_options Options for the tag.
   * @param  {string} [end='>']          The ending for the tag.
   * @return {string}              The resulting HTML.
   */
  tag: function (tag, html_options, end) {
    if (!end) {
      end = ">";
    }
    var txt = " ";
    for (var attr in html_options) {
      var value = "";
      if (html_options[attr] === false || html_options[attr]) {
        //as long as html_options[attr] is not null or undefined
        value = html_options[attr].toString();
      }
      if (attr == "Class") {
        // special case because "class" is a reserved word in IE
        attr = "class";
      }
      if (value.indexOf("'") !== -1) {
        txt += attr + '="' + value + '" ';
      } else {
        txt += attr + "='" + value + "' ";
      }
    }
    return "<" + tag + txt + end;
  },

  /**
   * Generate an end tag.
   * @param  {string} tag The tag name.
   * @return {string}     The resulting HTML.
   */
  tag_end: function (tag) {
    return "</" + tag + ">";
  },

  /**
   * Generates a text area tag.
   * @function
   * @param  {string} name         The name of the field
   * @param  {string} value        The value of the field
   * @param  {object} html_options Options for the tag.
   * @return {string}              The resulting HTML.
   */
  text_area_tag: textAreaTag,

  /**
   * Generates a text area tag.
   * @function
   * @param  {string} name         The name of the field
   * @param  {string} value        The value of the field
   * @param  {object} html_options Options for the tag.
   * @return {string}              The resulting HTML.
   */
  text_tag: textAreaTag,

  /**
   * Generates a text input tag.
   * @param  {string} name         The field name.
   * @param  {string} value        The field value.
   * @param  {object} html_options Options for the tag.
   * @return {string}              The resulting HTML.
   */
  text_field_tag: function (name, value, html_options) {
    return this.input_field_tag(name, value, "text", html_options);
  },

  /**
   * Generates a JavaScript snippet to change the browser location.
   * @param  {string} url The destination URL
   * @return {string}     The snippet.
   */
  url_for: function (url) {
    return 'window.location="' + url + '";';
  },

  /**
   * Generates an image tag.
   * @param  {string} image_location The image URL.
   * @param  {string} alt            The alternate text.
   * @param  {object} options        Options for the tag.
   * @return {string}                The resulting HTML.
   */
  img_tag: function (image_location, alt, options) {
    options = options || {};
    options.src = image_location;
    options.alt = alt;
    return this.single_tag_for("img", options);
  },

  /**
   * Generates a name for the given field name and prefix.
   * @param  {string} fieldName The field name.
   * @param  {string} [prefix]    The field name prefix.
   * @return {string}           The field name.
   */
  name_for: function (fieldName, prefix) {
    return HtmlUtils.nameFor(fieldName, prefix);
  },

  /**
   * Generates an id for the given field name and prefix.
   * @param  {string} fieldName The field name.
   * @param  {string} [prefix]    The field name prefix.
   * @return {string}           The field id.
   */
  id_for: function (fieldName, prefix) {
    return HtmlUtils.idFor(fieldName, prefix);
  },

  /**
   * Obtains the value for the given field name.
   * @param  {mixed} model     The model providing the data.
   * @param  {string} fieldName The field name.
   * @return {mixed}           The field value.
   */
  value_for: function (model, fieldName) {
    return HtmlUtils.valueFor(model, fieldName);
  },

  /**
   * Generates a select tag for the given model and field.
   * @param  {mixed} model        The model providing the data.
   * @param  {string} fieldName    The field name.
   * @param  {Array} options      The array of possible selections. Each item should be an object with a `value` and `text`.
   * @param  {object} html_options Options for the tag.
   * @param  {string} prefix       The field prefix.
   * @return {string}              The resulting HTML.
   */
  select_for: function (model, fieldName, options, html_options, prefix) {
    return this.select_tag(
      this.name_for(fieldName, prefix),
      this.value_for(model, fieldName),
      options,
      HtmlUtils.mergeAttributes(
        { id: this.id_for(fieldName, prefix) },
        html_options
      )
    );
  },

  /**
   * Generates an input tag for the given model and field.
   * @param  {mixed} model        The model providing the data.
   * @param  {string} fieldName    The field name.
   * @param  {string|Array} type      The field type or, for a select tag, the array of possible selections. Each item should be an object with a `value` and `text`.
   * @param  {object} html_options Options for the tag.
   * @param  {string} prefix       The field prefix.
   * @return {string}              The resulting HTML.
   */
  input_for: function (model, fieldName, type, html_options, prefix) {
    if (_.isArray(type)) {
      return this.select_for(model, fieldName, type, html_options, prefix);
    } else {
      var html_attrs;
      var inputType = type || "text";

      html_attrs = HtmlUtils.mergeAttributes(
        {
          type: inputType,
          id: this.id_for(fieldName, prefix),
          name: this.name_for(fieldName, prefix),
          value: this.value_for(model, fieldName),
        },
        html_options
      );

      if (inputType == "checkbox") {
        html_attrs.value = "true";
        if (this.value_for(model, fieldName)) {
          html_attrs.checked = true;
        }
      }

      if (html_attrs.type != "textarea") {
        return this.single_tag_for("input", html_attrs);
      } else {
        return this.text_area_tag(
          this.name_for(fieldName, prefix),
          this.value_for(model, fieldName),
          html_attrs
        );
      }
    }
  },

  /**
   * Generates a label for the given model and field name.
   * @param  {mixed} model        The model providing the data.
   * @param  {string} fieldName    The field name.
   * @param  {string} text      The label text.
   * @param  {object} html_options Options for the tag.
   * @param  {string} prefix       The field prefix.
   * @return {string}              The resulting HTML.
   */
  label_for: function (model, fieldName, text, html_options, prefix) {
    var html_attrs = HtmlUtils.mergeAttributes(
      {
        for: this.id_for(fieldName, prefix),
      },
      html_options
    );
    return this.tag("label", html_attrs) + text + this.tag_end("label");
  },

  /**
   * Generates the opening of a form group for the given model and field.
   * @param  {mixed} model        The model providing the data.
   * @param  {string} fieldName    The field name.
   * @param  {string} label      The label text.
   * @param  {string|Array} type      The field type or, for a select tag, the array of possible selections. Each item should be an object with a `value` and `text`.
   * @param  {object} html_options A hash containing a `wrapper`, a `label`, and an `input` property, each specifying HTML options for each particular element.
   * @param  {string} prefix       The field prefix.
   * @return {string}              The resulting HTML.
   */
  begin_form_group_for: function (
    model,
    fieldName,
    label,
    type,
    html_options,
    prefix
  ) {
    html_options = html_options || {};
    var wrapper_attrs = HtmlUtils.mergeAttributes(
      {
        class: "form-group",
      },
      html_options.wrapper
    );
    var labelEl, input;

    var labelAttrs = HtmlUtils.mergeAttributes(
      {
        class: "control-label",
      },
      html_options.label
    );
    var inputAttrs = HtmlUtils.mergeAttributes(
      {
        class: "form-control",
      },
      html_options.input
    );

    labelEl = this.label_for(model, fieldName, label, labelAttrs, prefix);

    if (_.isArray(type)) {
      input = this.select_for(model, fieldName, type, inputAttrs, prefix);
    } else {
      input = this.input_for(model, fieldName, type, inputAttrs, prefix);
    }

    var after = html_options.after;
    if (_.isFunction(after)) {
      after = after(model, fieldName); //to insert any content after the form control
    }
    if (!after) {
      after = "";
    }
    return this.tag("div", wrapper_attrs) + labelEl + input + after;
  },

  /**
   * Generates the end tag for a form group.
   * @return {string} The resulting HTML.
   */
  end_form_group: function () {
    return this.tag_end("div");
  },

  /**
   * Generates a form group for the given model and field.
   * @param  {mixed} model        The model providing the data.
   * @param  {string} fieldName    The field name.
   * @param  {string} label      The label text.
   * @param  {string|Array} type      The field type or, for a select tag, the array of possible selections. Each item should be an object with a `value` and `text`.
   * @param  {object} html_options A hash containing a `wrapper`, a `label`, and an `input` property, each specifying HTML options for each particular element.
   * @param  {string} prefix       The field prefix.
   * @return {string}              The resulting HTML.
   */
  form_group_for: function (
    model,
    fieldName,
    label,
    type,
    html_options,
    prefix
  ) {
    return (
      this.begin_form_group_for(
        model,
        fieldName,
        label,
        type,
        html_options,
        prefix
      ) + this.tag_end("div")
    );
  },

  /**
   * Generates a sequence of form groups for the given model and fields.
   * @param  {mixed} model        The model providing the data.
   * @param  {Array} fields    A mapping of field name to object with `label` and `type` values.
   * @param {string|Function} afterAll Markup or function to generate markup (given model and fieldName) to placed at the end of each form group.
   * @param  {string} prefix       The field prefix.
   * @return {string}              The resulting HTML.
   */
  form_groups_for: function (model, fields, afterAll, prefix) {
    var result = "";
    _.each(
      fields,
      function (field, fieldName) {
        result +=
          "\n" +
          this.form_group_for(
            model,
            fieldName,
            field.label,
            field.type,
            { after: afterAll },
            prefix
          );
      },
      this
    );
    return result;
  },

  /**
   * Generates the beginning of an accordion control.
   * @param  {string} name    A title for the control.
   * @param  {object} options Options for the control.
   * @param {object} options.html_options The options for the outermost tag.
   * @param {Boolean} options.open True to open the accordion initially, otherwise false.
   * @return {String}         The generated HTML.
   */
  begin_tab: function (name, options) {
    options = _.defaults({}, options, { open: false });
    var outer = this.start_tag_for(
      "div",
      HtmlUtils.mergeAttributes(
        {
          class:
            "slider-content " +
            (options.open ? "slider-open" : "slider-closed"),
        },
        options.html_options
      )
    );
    var title = this.start_tag_for("div", { class: "title-wrapper" });
    var titleInner = this.start_tag_for("div", { class: "slider-inner" });
    var titleHeading = this.start_tag_for("h2") + name + this.tag_end("h2");
    var titleTab = this.start_tag_for("div", { class: "slider-tab" });
    var wrapper = this.start_tag_for("div", {
      class: "slider-inner slider-wrapper",
    });
    var wrapperOuter = this.start_tag_for("div", { class: "slider-outer" });
    var wrapperContent = this.start_tag_for("div", { class: "slider-content" });
    titleInner +=
      titleHeading + titleTab + this.tag_end("div") + this.tag_end("div");
    title += titleInner + this.tag_end("div");

    wrapperOuter += wrapperContent;
    wrapper += wrapperOuter;

    outer += title + wrapper;
    return outer;
  },

  /**
   * Generates the closing of an accordion control.
   * @return {string} The generated HTML.
   */
  end_tab: function () {
    var closing = this.tag_end("div");
    var clear = this.start_tag_for("div", { class: "clear" });
    clear += closing;
    return closing + clear + closing + closing + closing;
  },

  /**
   * A reference to underscore.
   * @type {underscore}
   */
  _: _,

  /**
   * A reference to underscore.string
   * @type {underscore.string}
   */
  s: s,
};

Helper.template = function (templateFunction) {
  var helper = new Helper({}, {});

  return function (data) {
    var params = _.extend({}, helper, data);

    return templateFunction(params);
  };
};

export default Helper;



//////////////////
// WEBPACK FOOTER
// ./public/js/app/ejs.js
// module id = 101
// module chunks = 1